import React, { useState } from 'react';
import { Truck, MapPin, Phone, Mail, IndianRupee, ShieldCheck, Map, CheckCircle2, Menu, X, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { routes, whyChooseUs, trustFeatures } from '../mock';
import axios from 'axios';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Customer booking form state
  const [customerForm, setCustomerForm] = useState({
    pickupLocation: '',
    dropLocation: '',
    loadWeight: '',
    preferredDate: '',
    contactNumber: '',
    materialType: '',
    loadingUnloadingIncluded: 'no',
    notes: ''
  });

  // Truck owner return trip form state
  const [ownerForm, setOwnerForm] = useState({
    truckType: 'upto-3-tons',
    fromLocation: '',
    toLocation: '',
    availableDate: '',
    contactNumber: '',
    truckBodyType: 'open',
    expectedPrice: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const iconMap = {
    'indian-rupee': IndianRupee,
    'shield-check': ShieldCheck,
    'phone': Phone,
    'map': Map
  };

  const handleCustomerBooking = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/bookings`, customerForm);
      const result = response.data;
      
      toast({
        title: "Booking Submitted!",
        description: result.message,
      });
      
      setCustomerForm({
        pickupLocation: '',
        dropLocation: '',
        loadWeight: '',
        preferredDate: '',
        contactNumber: '',
        materialType: '',
        loadingUnloadingIncluded: 'no',
        notes: ''
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit booking. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOwnerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/return-trips`, ownerForm);
      const result = response.data;
      
      toast({
        title: "Return Trip Posted!",
        description: result.message,
      });
      
      setOwnerForm({
        truckType: 'upto-3-tons',
        fromLocation: '',
        toLocation: '',
        availableDate: '',
        contactNumber: '',
        truckBodyType: 'open',
        expectedPrice: ''
      });
    } catch (error) {
      console.error('Return trip error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to post return trip. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/919515698775', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* WhatsApp Sticky Bar - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-green-500 shadow-lg">
        <button
          onClick={openWhatsApp}
          className="w-full py-4 flex items-center justify-center gap-2 text-white font-semibold text-base"
        >
          <MessageCircle className="h-6 w-6" />
          Book Truck on WhatsApp – Chennai | Kadapa | Tirupati
        </button>
      </div>

      {/* WhatsApp Floating Button - Desktop Only */}
      <button
        onClick={openWhatsApp}
        className="hidden md:flex fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110 animate-pulse items-center justify-center"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Logo size={48} />
              <span className="text-2xl font-bold text-gray-900">Reload Freight</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 transition-colors">
                Home
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-gray-700 hover:text-blue-600 transition-colors">
                How It Works
              </button>
              <button onClick={() => scrollToSection('book-truck')} className="text-gray-700 hover:text-blue-600 transition-colors">
                Book a Truck
              </button>
              <button onClick={() => scrollToSection('post-trip')} className="text-gray-700 hover:text-blue-600 transition-colors">
                Post Return Trip
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors">
                Contact
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">
                Home
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">
                How It Works
              </button>
              <button onClick={() => scrollToSection('book-truck')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">
                Book a Truck
              </button>
              <button onClick={() => scrollToSection('post-trip')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">
                Post Return Trip
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">
                Contact
              </button>
            </div>
          )}
        </nav>
      </header>

      {/* Main H1 Section - SEO */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Return Load Transport – Chennai to Kadapa & Tirupati
          </h1>
          <p className="text-gray-600 text-base md:text-lg">
            Reload Freight provides reliable truck and freight transport services on selected high-demand routes. We focus on limited routes to ensure verified trucks, timely pickup, and consistent service quality.
          </p>
          <p className="text-gray-600 text-sm mt-2">
            We also support return load and freight transport for Tirupati (TPT).
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section id="home" className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
              Save Money on Truck Transport<br />with Return Loads
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              Book reliable trucks at lower prices by using empty return trips.
            </p>
            <p className="text-base text-gray-500">
              Currently serving Chennai ↔ Kadapa and Tirupati (TPT)
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => scrollToSection('book-truck')} 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-lg hover:shadow-xl"
              >
                Book a Truck
              </Button>
              <Button 
                onClick={() => scrollToSection('post-trip')} 
                size="lg" 
                variant="outline" 
                className="border-gray-300 hover:border-blue-600 hover:text-blue-600 transition-colors"
              >
                Post Return Trip (Truck Owners)
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Return Load Headline Section */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
            Return Load Booking Made Easy
          </h3>
          <p className="text-lg text-gray-700 mb-4">
            Reload Freight helps customers find return load trucks from Chennai to Kadapa and Tirupati at lower cost.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 text-gray-600 mb-6">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>Talk directly with truck owners.</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>No advance. Only confirmed loads.</span>
            </div>
          </div>
          <Button 
            onClick={() => window.open('https://wa.me/919515698775?text=Hi,%20I%20need%20a%20return%20load%20truck', '_blank')}
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            WhatsApp for Return Load Booking
          </Button>
          {/* Trust Line */}
          <p className="mt-4 text-gray-600 font-medium">
            ✓ Verified transporters • ✓ Transparent pricing • ✓ Route-focused service
          </p>
          {/* MSME Trust Line */}
          <p className="mt-2 text-gray-500 text-sm">
            Serving MSMEs, traders, and transporters on verified routes.
          </p>
          {/* Micro-trust - Phone option */}
          <p className="mt-3 text-gray-600">
            📞 Prefer call? <a href="tel:+919515698775" className="text-blue-600 font-semibold hover:underline">+91 9515698775</a>
          </p>
          {/* Response time trust */}
          <p className="mt-2 text-gray-500 text-sm">
            ⏱ Response within 10–15 minutes
          </p>
          {/* Internal Link to Routes */}
          <p className="mt-4">
            <a href="#routes" onClick={(e) => { e.preventDefault(); document.getElementById('routes')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-blue-600 hover:underline font-medium">
              View our truck routes in Chennai, Kadapa & Tirupati →
            </a>
          </p>
          {/* SEO Text */}
          <p className="mt-4 text-sm text-gray-500 max-w-2xl mx-auto">
            Reload Freight is a return load transport service connecting customers with empty return trucks from Chennai to Kadapa. If you are searching for a return truck, empty truck, or low-cost lorry transport from Chennai to Kadapa, we help you connect directly with verified truck owners at affordable rates.
          </p>
        </div>
      </section>

      {/* Why Reload Freight Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Reload Freight?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="text-center hover:shadow-xl transition-shadow border-gray-200">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* For Customers */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-2xl text-blue-600">For Customers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      1
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Enter pickup & drop location</h4>
                      <p className="text-gray-600">Tell us where you need the truck</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      2
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Share load details (weight, date)</h4>
                      <p className="text-gray-600">Provide information about your cargo</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      3
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Get matched with a return truck</h4>
                      <p className="text-gray-600">We connect you with available trucks</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      4
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Confirm & transport</h4>
                      <p className="text-gray-600">Complete the booking and get your goods delivered</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* For Truck Owners */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-2xl text-green-600">For Truck Owners</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      1
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Post your return route</h4>
                      <p className="text-gray-600">Share your empty return trip details</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      2
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Get customer requests</h4>
                      <p className="text-gray-600">Receive inquiries from customers</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      3
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Negotiate & confirm</h4>
                      <p className="text-gray-600">Discuss terms directly with customers</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      4
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 mb-1">Earn on empty trips</h4>
                      <p className="text-gray-600">Make money on return journeys</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Book a Truck Section */}
      <section id="book-truck" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-700 mb-4">For Customers</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Book a Truck
            </h2>
            <p className="text-lg text-gray-600">
              Fill out the form below and our team will contact you shortly.
            </p>
          </div>

          <Card className="shadow-xl border-gray-200">
            <CardContent className="p-8">
              <form onSubmit={handleCustomerBooking} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pickup Location * (🎤 You can speak)
                    </label>
                    <Input
                      required
                      placeholder="Speak or type: Chennai"
                      value={customerForm.pickupLocation}
                      onChange={(e) => setCustomerForm({ ...customerForm, pickupLocation: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Drop Location * (🎤 You can speak)
                    </label>
                    <Input
                      required
                      placeholder="Speak or type: Kadapa"
                      value={customerForm.dropLocation}
                      onChange={(e) => setCustomerForm({ ...customerForm, dropLocation: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 -mt-4">Example: "Chennai to Kadapa tomorrow"</p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Load Weight (kg / tons) *
                    </label>
                    <Input
                      required
                      placeholder="e.g., 2 tons"
                      value={customerForm.loadWeight}
                      onChange={(e) => setCustomerForm({ ...customerForm, loadWeight: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Date *
                    </label>
                    <Input
                      required
                      type="date"
                      value={customerForm.preferredDate}
                      onChange={(e) => setCustomerForm({ ...customerForm, preferredDate: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-blue-500"
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number *
                  </label>
                  <Input
                    required
                    type="tel"
                    placeholder="+91-XXXXXXXXXX"
                    value={customerForm.contactNumber}
                    onChange={(e) => setCustomerForm({ ...customerForm, contactNumber: e.target.value })}
                    className="transition-all focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material Type (Optional)
                    </label>
                    <Input
                      placeholder="e.g., Electronics, Furniture"
                      value={customerForm.materialType}
                      onChange={(e) => setCustomerForm({ ...customerForm, materialType: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loading / Unloading Included?
                    </label>
                    <select
                      value={customerForm.loadingUnloadingIncluded}
                      onChange={(e) => setCustomerForm({ ...customerForm, loadingUnloadingIncluded: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                      <option value="negotiable">Negotiable</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Route / Load Details (You can speak 🎤)
                  </label>
                  <Textarea
                    placeholder="Speak or type: Chennai to Kadapa tomorrow 3 ton"
                    value={customerForm.notes}
                    onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                    rows={4}
                    className="transition-all focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    🎙️ <strong>Voice Command (Beta)</strong> – You can speak instead of typing using your mobile keyboard mic. Advanced voice features are under development.
                    <br />Supports English voice. Telugu voice support coming soon.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Booking'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Post Return Trip Section */}
      <section id="post-trip" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-green-100 text-green-700 mb-4">For Truck Owners</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Post Return Trip
            </h2>
            <p className="text-lg text-gray-600">
              Earn money on your empty return trips by connecting with customers.
            </p>
          </div>

          <Card className="shadow-xl border-gray-200">
            <CardContent className="p-8">
              <form onSubmit={handleOwnerSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Truck Type *
                  </label>
                  <select
                    required
                    value={ownerForm.truckType}
                    onChange={(e) => setOwnerForm({ ...ownerForm, truckType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                  >
                    <option value="upto-3-tons">Up to 3 tons</option>
                    <option value="3-to-6-tons">3 to 6 tons</option>
                    <option value="6-to-10-tons">6 to 10 tons</option>
                    <option value="above-10-tons">Above 10 tons</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From Location * (🎤 You can speak)
                    </label>
                    <Input
                      required
                      placeholder="Speak or type: Chennai"
                      value={ownerForm.fromLocation}
                      onChange={(e) => setOwnerForm({ ...ownerForm, fromLocation: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Location * (🎤 You can speak)
                    </label>
                    <Input
                      required
                      placeholder="Speak or type: Kadapa"
                      value={ownerForm.toLocation}
                      onChange={(e) => setOwnerForm({ ...ownerForm, toLocation: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 -mt-4">Example: "Chennai to Kadapa tomorrow"</p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available Date *
                    </label>
                    <Input
                      required
                      type="date"
                      value={ownerForm.availableDate}
                      onChange={(e) => setOwnerForm({ ...ownerForm, availableDate: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-green-500"
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Number *
                    </label>
                    <Input
                      required
                      type="tel"
                      placeholder="+91-XXXXXXXXXX"
                      value={ownerForm.contactNumber}
                      onChange={(e) => setOwnerForm({ ...ownerForm, contactNumber: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Truck Body Type (Optional)
                    </label>
                    <select
                      value={ownerForm.truckBodyType}
                      onChange={(e) => setOwnerForm({ ...ownerForm, truckBodyType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                    >
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                      <option value="container">Container</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Price (₹) (Optional)
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 15000"
                      value={ownerForm.expectedPrice}
                      onChange={(e) => setOwnerForm({ ...ownerForm, expectedPrice: e.target.value })}
                      className="transition-all focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-green-600 hover:bg-green-700 text-white transition-colors"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Posting...' : 'Post Return Trip'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  🎙️ <strong>Voice Command (Beta)</strong> – You can speak instead of typing using your mobile keyboard mic. Advanced voice features are under development.
                  <br />Supports English voice. Telugu voice support coming soon.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Routes We Serve */}
      <section id="routes" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Chennai, Kadapa & Tirupati Truck Routes We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Reload Freight provides reliable truck and freight transport services on selected high-demand routes. We focus on limited routes to ensure verified trucks, timely pickup, and consistent service quality.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Chennai ↔ Kadapa Routes */}
            {routes.map((route) => (
              <Card key={route.id} className="border-gray-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <MapPin className="w-8 h-8 text-blue-600" />
                      <div>
                        <div className="font-bold text-xl text-gray-900">{route.from} → {route.to}</div>
                        <div className="text-sm text-gray-600">{route.distance}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Tirupati Routes */}
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <MapPin className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="font-bold text-xl text-gray-900">Chennai → Tirupati (TPT)</div>
                    <div className="text-sm text-gray-600">135 km</div>
                    <div className="text-sm text-gray-500 mt-1">Regular freight movement for businesses and traders.</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <MapPin className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="font-bold text-xl text-gray-900">Tirupati (TPT) → Chennai</div>
                    <div className="text-sm text-gray-600">135 km</div>
                    <div className="text-sm text-gray-500 mt-1">Suitable for return loads and intercity transport.</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Supporting paragraph */}
          <div className="mt-8 max-w-3xl mx-auto">
            <p className="text-center text-gray-600 leading-relaxed">
              In addition to Chennai–Kadapa routes, Reload Freight also supports freight movement for Tirupati (TPT). Our route-based model helps maintain service reliability and fair pricing.
            </p>
          </div>

          <div className="mt-8 max-w-2xl mx-auto">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <p className="text-center text-gray-700 leading-relaxed">
                  <strong>Quality Over Quantity:</strong> We're building a strong network on these routes first to ensure 
                  excellent service, reliability, and trust before expanding. More routes coming soon based on demand.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust & Safety Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trust & Safety
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustFeatures.map((feature) => (
              <Card key={feature.id} className="border-gray-200 text-center">
                <CardContent className="p-6">
                  <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Legal Disclaimer */}
          <div className="mt-12 max-w-3xl mx-auto">
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-6">
                <p className="text-center text-gray-700 text-sm leading-relaxed">
                  <strong>Important:</strong> Reload Freight is a discovery platform connecting customers and truck owners. 
                  We do not handle payments, transportation, or goods. All agreements and transactions are directly between users.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Contact Us
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a href="tel:+919515698775" className="text-blue-600 hover:underline">
                  +91 9515698775
                </a>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a href="mailto:support@reloadfreight.in" className="text-orange-600 hover:underline break-all">
                  support@reloadfreight.in
                </a>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
                <button 
                  onClick={openWhatsApp}
                  className="text-green-600 hover:underline"
                >
                  Click to Chat
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {/* FAQ 1 */}
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  What is a return load?
                </h3>
                <p className="text-gray-600">
                  A return load is when a truck that has delivered goods to a destination picks up a new load for the return journey instead of going back empty. This helps truck owners earn more and allows customers to transport goods at lower rates since the truck is already making the trip.
                </p>
              </CardContent>
            </Card>

            {/* FAQ 2 */}
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Is return load transport cheaper?
                </h3>
                <p className="text-gray-600">
                  Yes! Return load transport can be 20-40% cheaper than regular truck booking. Since the truck is already traveling back, owners offer discounted rates to cover fuel costs rather than returning empty. You save money while truck owners earn extra income – it's a win-win.
                </p>
              </CardContent>
            </Card>

            {/* FAQ 3 */}
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Which routes do you serve?
                </h3>
                <p className="text-gray-600">
                  Currently, we focus on <strong>Chennai to Kadapa</strong> and <strong>Chennai to Tirupati (TPT)</strong> routes. We're building a strong, reliable network on these routes first before expanding. More routes will be added soon based on customer demand.
                </p>
              </CardContent>
            </Card>

            {/* FAQ 4 - Tirupati */}
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Do you provide truck booking for Tirupati (TPT)?
                </h3>
                <p className="text-gray-600">
                  Yes, Reload Freight offers truck and freight booking services for Tirupati and surrounding areas.
                </p>
              </CardContent>
            </Card>

            {/* FAQ 5 - Truck Types */}
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  What types of trucks are available?
                </h3>
                <p className="text-gray-600">
                  Mini trucks and commercial trucks based on load size and route.
                </p>
              </CardContent>
            </Card>

            {/* FAQ 6 - How to Book */}
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  How can I book a truck?
                </h3>
                <p className="text-gray-600">
                  You can book quickly by contacting us on WhatsApp.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Logo size={36} />
              <span className="text-xl font-bold">Reload Freight</span>
            </div>
            <p className="text-gray-300">
              © 2025 Reload Freight Technologies Private Limited
            </p>
            <p className="text-gray-400">
              All rights reserved
            </p>
            {/* Phone Number - Plain Text (SEO important) */}
            <p className="text-gray-300 text-lg font-medium">
              📞 +91 9515698775
            </p>
            <div className="flex justify-center space-x-1 text-gray-400 text-sm">
              <a href="tel:+919515698775" className="hover:text-white transition-colors">Call Now</a>
              <span>•</span>
              <a href="mailto:support@reloadfreight.in" className="hover:text-white transition-colors">support@reloadfreight.in</a>
            </div>
            <div className="flex justify-center space-x-4 text-gray-400 pt-2 text-sm">
              <a href="/terms-conditions" className="hover:text-white transition-colors">Terms & Conditions</a>
              <span>•</span>
              <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
